(ns practitest.frontend.components.list-builder
  (:require
    ["@dnd-kit/core" :refer [DndContext DragOverlay]]
    ["@dnd-kit/sortable" :refer [SortableContext arrayMove useSortable verticalListSortingStrategy]]
    ["@dnd-kit/utilities" :as dnd-utilities]
    [clojure.string :as str]
    [practitest.frontend.common.i18n :refer [get-translation]]
    [practitest.frontend.common.dom :as dom]
    [practitest.frontend.components.icons :as icons]
    [practitest.frontend.components2.buttons :as buttons]
    [practitest.frontend.subs :as subs]
    [re-frame.core :as re-frame]
    [reagent.core :as r]))

(defn list-item
  [{:keys [ref drag-handle-listeners class transform transition on-delete on-edit]} item]
  [:r> "div" drag-handle-listeners
  [:div {:ref ref
        :class class
        :key (:id item)
        :style {:transition transition
                :transform transform}}
   [icons/svg-grip-vertical-reduced]
   [:div.flex-grow-1 (:value item)]
   (when on-edit
     [:button.btn.edit-btn {:type "button"
                            :on-pointer-down (fn [e] (.stopPropagation e)
                                               (on-edit (:value item)))}
      [icons/mux-pencil-rename]])
   [:button.btn {:type "button"
                 :disabled false
                 :on-pointer-down (fn [e] (.stopPropagation e)
                                    (on-delete (:value item)))}
    [icons/mux-trash]]]])

(defn sortable-item [{:keys [id value on-delete-item on-edit-item] :as props}]
  (let [sortable-context (useSortable #js {:id id})
        active-id (some-> sortable-context .-active .-id)]
    [:f> list-item
     {:ref (.-setNodeRef sortable-context)
      :drag-handle-listeners (.-listeners sortable-context)
      :class (str "list-group-item"
                  (when (= active-id id) " dragging-item"))
      :transition (.-transition sortable-context)
      :transform (dnd-utilities/CSS.Translate.toString (.-transform sortable-context))
      :on-delete on-delete-item
      :on-edit on-edit-item}
     props]))

(defn sortable-items-component
  [view-items items delete-item on-order-change edit-item]
  (let [items-array (into-array view-items)
        active-id   (r/atom nil)]
    [:r> DndContext
     #js {:onDragStart (fn [e] (reset! active-id (some-> e .-active .-id)))
          :onDragEnd (fn [e]
                       (let [active-id (some-> e .-active .-id)
                             over-id   (some-> e .-over .-id)]
                         (when-not (= active-id over-id)
                           (let [old-index (.indexOf items-array active-id)
                                 new-index (.indexOf items-array over-id)]
                             (on-order-change (arrayMove (clj->js items) old-index new-index))))))}
     [:r> SortableContext
      #js {:items items-array
           :strategy verticalListSortingStrategy}
      (into
        [:div]
        (map (fn [item]
               [:f> sortable-item
                {:id item
                 :value item
                 :on-delete-item delete-item
                 :on-edit-item edit-item}]))
        view-items)]
     [:r> DragOverlay #js {:dropAnimation nil}
      (when @active-id
        (let [item (first (filter #(= @active-id %) view-items))]
          (r/as-element
            [:f> list-item {} item])))]]))

(defn view-panel [field-id form-props delete-item add-item & [parent-list-item edit-item]]
  (r/with-let [input-value (r/atom "")
               sort-type (r/atom nil)]
    (let [edit-item (when @(re-frame/subscribe [::subs/edit-global-field-values?]) edit-item)
          {:keys [get-field-state set-field-state]} form-props
          field-value (:value @(get-field-state field-id))
          set-items (fn [values] (let [new-values {:value (if parent-list-item
                                                            (assoc (or field-value {}) parent-list-item values)
                                                            values)
                                                   :dirty? true}]
                                   (set-field-state field-id new-values)))
          on-order-change (fn [new-items] (set-items (vec new-items))
                            (reset! sort-type nil))
          items (cond
                  (vector? field-value) field-value
                  (map? field-value) (or (get field-value parent-list-item) [])
                  :else [])
          view-items  (filter #(str/starts-with? % @input-value) items)
          arrow-icon (case @sort-type
                       "DESC" [icons/mux-arrow-down]
                       "ASC"  [icons/mux-arrow-up]
                       nil)]

      [:div.list-builder-container
       (when parent-list-item
         [:div (get-translation "global-field.form-list.sud-title" "Child Values") ":"])
       [:div.flex
        [:input.search-input {:placeholder (get-translation "global-field.form-list.placeholder" "Search / Add New")
                              :value @input-value
                              :on-change (fn [e] (reset! input-value (dom/event-value e)))
                              :on-key-down (fn [e] (when (= "Enter" (.-key e))
                                                     (add-item input-value)
                                                     (set-items (conj items @input-value))
                                                     (reset! input-value "")))}]
        [buttons/button {:class "add-button"
                         :primary? true
                         :icon [icons/mux-add-step]
                         :disabled? (or (str/blank? @input-value)
                                        (some #(= % @input-value) items))
                         :on-click (fn [] (add-item input-value)
                                     (set-items (conj items @input-value))
                                     (reset! input-value ""))}]
        [buttons/button {:class "sort-button"
                         :tertiary? true
                         :text (get-translation "global-field.form-list.sort-values" "Sort values above (by ABC)")
                         :end-icon arrow-icon
                         :disabled? (or (empty? view-items) (= items nil))
                         :on-click #(let [new-sort (if (= @sort-type "ASC") "DESC" "ASC")
                                          sorted-items (case new-sort
                                                         "ASC" (sort-by str/lower-case items)
                                                         "DESC" (reverse (sort-by str/lower-case items))
                                                         items)]
                                      (reset! sort-type new-sort)
                                      (set-items (vec sorted-items)))}]]

                 (when (not-empty items)
                  [:div.list-items-container
                   (if (empty? view-items)
                     [:div.info-message
                      (get-translation "global-field.form-list.placeholder-message" "No matching values found - you can add it using the + button above")]
                     [:div.list-block
                      [:f> sortable-items-component
                      view-items
                      items
                      delete-item
                      on-order-change
                      edit-item]])])])))
